<div class="space-y-6">
    <div class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Security &amp; configuration</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                Manage API keys for your LLM providers and optional auth tokens for external chatbot access.
            </p>
        </div>
    </div>

    @if (invalidBot()) {
    <div
        class="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white/60 dark:bg-gray-900/40 px-5 py-10 text-center">
        <app-icon-v2 name="shield-off" [size]="36" class="mx-auto text-gray-400 dark:text-gray-500 mb-3" />
        <p class="text-sm font-medium text-gray-800 dark:text-gray-200">No chatbot selected</p>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
            Open an existing chatbot from the list to manage security settings.
        </p>
    </div>
    } @else if (pageLoading()) {
    <div class="space-y-8">
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5">
            <div class="h-4 w-40 rounded bg-gray-200 dark:bg-gray-700 animate-pulse mb-4"></div>
            <div class="h-3 w-full max-w-md rounded bg-gray-100 dark:bg-gray-700/80 animate-pulse mb-6"></div>
            <div class="h-10 rounded-lg bg-gray-100 dark:bg-gray-700/80 animate-pulse"></div>
        </div>
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5">
            <div class="h-4 w-48 rounded bg-gray-200 dark:bg-gray-700 animate-pulse mb-4"></div>
            <div class="space-y-3">
                <div class="h-14 rounded-lg bg-gray-100 dark:bg-gray-700/80 animate-pulse"></div>
                <div class="h-14 rounded-lg bg-gray-100 dark:bg-gray-700/80 animate-pulse"></div>
            </div>
        </div>
    </div>
    } @else if (pageError()) {
    <div
        class="rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/80 dark:bg-red-950/30 px-5 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div class="flex gap-3 min-w-0">
            <app-icon-v2 name="circle-alert" [size]="22" class="shrink-0 text-red-600 dark:text-red-400 mt-0.5" />
            <div>
                <p class="text-sm font-medium text-red-900 dark:text-red-100">Something went wrong</p>
                <p class="text-xs text-red-700/90 dark:text-red-300/90 mt-0.5">{{ pageError() }}</p>
            </div>
        </div>
        <app-button variant="outline" className="shrink-0" [disabled]="pageLoading()" (click)="retryLoad()">
            Retry
        </app-button>
    </div>
    } @else {
    <div class="space-y-8">
        <!-- Section: Auth token -->
        <section
            class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm overflow-hidden">
            <div
                class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80 flex items-start justify-between gap-4">
                <div class="flex items-start gap-3 min-w-0 flex-1">
                    <div
                        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500/15 to-indigo-500/15 dark:from-purple-500/25 dark:to-indigo-500/20 border border-purple-500/20">
                        <app-icon-v2 name="key-round" [size]="20" class="text-purple-600 dark:text-purple-400" />
                    </div>
                    <div class="min-w-0 flex-1">
                        <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Auth token</h3>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Optional bearer token for authenticated access. Turn on to generate a token; turn off to
                            revoke
                            access for API clients.
                        </p>
                    </div>
                </div>
                @if (authToken(); as auth) {
                @for (r of [authToggleReset()]; track r) {
                <div class="flex shrink-0 items-center gap-2 pt-0.5">
                    <span
                        class="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap hidden sm:inline">
                        {{ auth.enabled ? 'Enabled' : 'Off' }}
                    </span>
                    <app-toggle [checked]="auth.enabled" [disabled]="authActionLoading()"
                        (toggleChange)="onAuthTokenToggle($event)" />
                </div>
                }
                }
            </div>
            <div class="px-5 py-5">
                @if (authToken(); as auth) {
                @if (!auth.enabled) {
                <p class="text-sm text-gray-600 dark:text-gray-300">
                    Turn on the switch above to generate a bearer token for authenticated API access.
                </p>
                } @else {
                <div class="space-y-4">
                    <div class="flex flex-wrap items-center gap-2">
                        <span
                            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                            Active
                        </span>
                        @if (auth.expiryDate) {
                        <span class="text-xs text-gray-500 dark:text-gray-400">
                            Expires {{ auth.expiryDate | globalDateFormat:'mediumDateTime' }}
                        </span>
                        }
                    </div>
                    @if (auth.token) {
                    <div
                        class="flex flex-col gap-2 sm:flex-row sm:items-stretch rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/50 p-3">
                        <code
                            class="flex-1 min-w-0 text-xs sm:text-sm font-mono text-gray-800 dark:text-gray-100 break-all px-1 py-1.5">
                            {{ tokenRevealed() ? auth.token : maskedToken(auth.token) }}
                        </code>
                        <div class="flex flex-wrap items-center gap-2 shrink-0">
                            <button type="button"
                                class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                [disabled]="rotateTokenLoading() || authActionLoading()" (click)="rotateAuthToken()">
                                <app-icon-v2 name="refresh-cw" [size]="14"
                                    [customClass]="rotateTokenLoading() ? 'animate-spin' : ''" />
                                {{ rotateTokenLoading() ? 'Rotating…' : 'Rotate token' }}
                            </button>
                            <button type="button"
                                class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                [disabled]="rotateTokenLoading() || authActionLoading()" (click)="toggleTokenReveal()">
                                <app-icon-v2 [name]="tokenRevealed() ? 'eye-off' : 'eye'" [size]="14" />
                                {{ tokenRevealed() ? 'Hide' : 'Reveal' }}
                            </button>
                            <button type="button"
                                class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                [disabled]="rotateTokenLoading() || authActionLoading()"
                                (click)="copyToken(auth.token)">
                                <app-icon-v2 name="copy" [size]="14" />
                                Copy
                            </button>
                        </div>
                    </div>
                    } @else {
                    <p class="text-sm text-amber-700 dark:text-amber-300/90">
                        Token is enabled but not returned here. Refresh the page or contact support if this persists.
                    </p>
                    }
                </div>
                }
                } @else {
                <p class="text-sm text-gray-500 dark:text-gray-400">Loading auth status…</p>
                }
            </div>
        </section>

        <!-- Section: Provider keys -->
        <section
            class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm overflow-hidden">
            @if (hasMissingProviderKeys()) {
            <div
                class="px-4 py-3 sm:px-5 border-b border-amber-200/90 dark:border-amber-800/60 bg-gradient-to-r from-amber-50 via-orange-50/90 to-amber-50 dark:from-amber-950/55 dark:via-orange-950/40 dark:to-amber-950/55 flex gap-2">
                <p class="text-sm font-semibold text-amber-950 dark:text-amber-100">API keys required: </p>
                <p class="text-xs text-amber-900/90 dark:text-amber-200/90 mt-0.5 leading-relaxed">
                    This chatbot will not work until every provider below has a valid API key configured. Add or
                    replace keys in each row as needed.
                </p>
            </div>
            }
            <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80 flex items-start gap-3">
                <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500/15 to-blue-500/15 dark:from-sky-500/25 dark:to-blue-500/20 border border-sky-500/20">
                    <app-icon-v2 name="cpu" [size]="20" class="text-sky-600 dark:text-sky-400" />
                </div>
                <div class="min-w-0 flex-1">
                    <h3 class="text-sm font-semibold text-gray-900 dark:text-white">AI provider keys</h3>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Keys are encrypted on the server. Use “Replace key” to rotate credentials.
                    </p>
                </div>
            </div>
            <div class="px-5 py-5">
                @if (providers().length === 0) {
                <div class="text-center py-8 px-2">
                    <app-icon-v2 name="layers" [size]="32" class="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <p class="text-sm text-gray-600 dark:text-gray-300">No LLM providers in use for this workflow yet.
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-500 mt-1 max-w-sm mx-auto">
                        Configure providers in your agent or workflow; they will appear here for key management.
                    </p>
                </div>
                } @else {
                <ul
                    class="divide-y divide-gray-100 dark:divide-gray-700/80 rounded-lg border border-gray-100 dark:border-gray-700/80 overflow-hidden">
                    @for (p of providers(); track p.providerId) {
                    <li class="bg-white/50 dark:bg-gray-900/20">
                        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3.5">
                            <div class="min-w-0 flex-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <span class="text-sm font-medium text-gray-900 dark:text-white truncate">{{
                                        p.providerName }}</span>
                                    @if (!p.keyConfigured) {
                                    <span
                                        class="inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-900 dark:bg-amber-900/35 dark:text-amber-200">
                                        API key required
                                    </span>
                                    }
                                </div>
                                @if (p.keyConfigured && p.apiKeyEncSliced) {
                                <p class="text-xs font-mono text-gray-600 dark:text-gray-400 mt-1 truncate">
                                    {{ p.apiKeyEncSliced }}
                                </p>
                                }
                            </div>
                            <app-button variant="outline" size="sm" className="shrink-0 w-full sm:w-auto justify-center"
                                (click)="toggleExpandProvider(p.providerId)">
                                {{ expandedProviderId() === p.providerId ? 'Cancel' : (p.keyConfigured ? 'Replace key' :
                                'Set key') }}
                            </app-button>
                        </div>
                        @if (expandedProviderId() === p.providerId) {
                        <div
                            class="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-950/30">
                            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1.5">API
                                key</label>
                            <input type="password" autocomplete="off"
                                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 dark:focus:border-purple-400"
                                placeholder="Paste provider API key" [ngModel]="providerKeyInput()"
                                (ngModelChange)="providerKeyInput.set($event)" />
                            <div class="flex flex-wrap gap-2 mt-3">
                                <app-button variant="primary" size="sm" [disabled]="providerKeySaving()"
                                    (click)="saveProviderKey()">
                                    {{ providerKeySaving() ? 'Saving…' : 'Save key' }}
                                </app-button>
                                <app-button variant="ghost" size="sm" [disabled]="providerKeySaving()"
                                    (click)="toggleExpandProvider(p.providerId)">
                                    Cancel
                                </app-button>
                            </div>
                        </div>
                        }
                    </li>
                    }
                </ul>
                }
            </div>
        </section>

        <!-- Section: User access -->
        <section
            class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm relative">
            <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/80 flex items-start justify-between gap-4">
                <div class="flex items-start gap-3 min-w-0 flex-1">
                    <div
                        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500/15 to-orange-500/15 dark:from-amber-500/25 dark:to-orange-500/20 border border-amber-500/20">
                        <app-icon-v2 name="users" [size]="20" class="text-amber-600 dark:text-amber-400" />
                    </div>
                    <div class="min-w-0 flex-1">
                        <h3 class="text-sm font-semibold text-gray-900 dark:text-white">User access</h3>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Select which organization members have access to this chatbot.
                        </p>
                    </div>
                </div>
                @if (accessManagementLoading()) {
                    <div class="flex items-center gap-2 text-[10px] font-medium text-amber-600 dark:text-amber-400 animate-pulse bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded-full border border-amber-100 dark:border-amber-800/50">
                        <app-icon-v2 name="refresh-cw" [size]="12" class="animate-spin" />
                        Syncing...
                    </div>
                }
            </div>

            <div class="px-5 py-5 space-y-6">
                <!-- Selected Users Chips -->
                <div>
                    <label class="block text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                        Currently assigned access
                    </label>
                    <div class="flex flex-wrap gap-2 min-h-[40px] p-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/30 dark:bg-gray-900/20">
                        @if (selectedUsers().length === 0) {
                            <div class="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 italic py-1">
                                <app-icon-v2 name="info" [size]="14" />
                                No users selected. Use the search below to grant access.
                            </div>
                        } @else {
                            @for (user of selectedUsers(); track user.pid) {
                                <span class="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-100 dark:border-amber-800/50 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors group">
                                    {{ user.firstName }} {{ user.lastName }}
                                    <button type="button" 
                                        (click)="removeUserFromChips(user.pid)" 
                                        [disabled]="accessManagementLoading()"
                                        class="flex h-5 w-5 items-center justify-center rounded-full hover:bg-amber-200 dark:hover:bg-amber-800 text-amber-400 hover:text-amber-700 dark:text-amber-500 dark:hover:text-amber-200 transition-all disabled:opacity-50">
                                        <app-icon-v2 name="x" [size]="12" />
                                    </button>
                                </span>
                            }
                        }
                    </div>
                </div>

                <!-- Selection Dropdown Area -->
                <div class="relative">
                    <button type="button" 
                        (click)="isUserSelectionExpanded.set(!isUserSelectionExpanded())"
                        class="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm transition-all hover:border-amber-500/50 dark:hover:border-amber-500/50 focus:outline-none focus:ring-2 focus:ring-amber-500/10 group">
                        <div class="flex items-center gap-2">
                            <app-icon-v2 name="plus" [size]="16" class="text-gray-400 group-hover:text-amber-500 transition-colors" />
                            <span [class.text-gray-400]="selectedUsers().length === 0" class="font-medium text-gray-700 dark:text-gray-200">
                                {{ selectedUsers().length ? 'Manage ' + selectedUsers().length + ' users' : 'Grant access to users...' }}
                            </span>
                        </div>
                        <app-icon-v2 [name]="isUserSelectionExpanded() ? 'chevron-up' : 'chevron-down'" [size]="16" class="text-gray-400 transition-transform" />
                    </button>

                    @if (isUserSelectionExpanded()) {
                        <div 
                            (click)="$event.stopPropagation()"
                            class="absolute z-[100] left-0 right-0 mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-xl max-h-[340px] flex flex-col overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                            <!-- Search -->
                            <div class="p-3 border-b border-gray-100 dark:border-gray-800">
                                <div class="relative">
                                    <app-icon-v2 name="search" [size]="14" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="text" 
                                           [ngModel]="userSearchQuery()"
                                           (ngModelChange)="userSearchQuery.set($event)"
                                           placeholder="Search team members..."
                                           class="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-lg text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500 dark:focus:border-amber-500 outline-none transition-all" />
                                </div>
                            </div>

                            <!-- Options List -->
                            <div class="overflow-y-auto flex-1 py-1 custom-scrollbar">
                                <app-selection 
                                    [options]="filteredOptions()" 
                                    [mode]="'multiple'"
                                    [variant]="'default'"
                                    [ngModel]="accessUserIds()"
                                    (valueChange)="onSelectionChange($event)"
                                    class="px-2 block" />
                                
                                @if (filteredOptions().length === 0) {
                                    <div class="px-4 py-10 text-center">
                                        <app-icon-v2 name="search" [size]="24" class="mx-auto text-gray-300 mb-2 opacity-50" />
                                        <p class="text-xs text-gray-500">No members found for "{{ userSearchQuery() }}"</p>
                                    </div>
                                }
                            </div>

                            <!-- Footer -->
                            <div class="p-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                                <button type="button" 
                                        (click)="isUserSelectionExpanded.set(false)"
                                        class="w-full py-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-amber-600 transition-colors">
                                    Close List
                                </button>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </section>
    </div>
    }
</div>